import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../Modal';
import Loader from '../Loader';
import ExportBtn from '../ExportBtn';
import API from '../../api/smsGateway';
import translations from '../../translations/de';
import useLockBodyScroll from '../../customHooks/useLockBodyScroll';
import formatTime from './formatTime';

const formatDuration = (from, to) => {
    const minutes = Math.max(1, Math.round((new Date(to) - new Date(from)) / 60000));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const parts = [];
    if (days) parts.push(`${days} ${translations.days_short}`);
    if (hours) parts.push(`${hours} ${translations.hours_short}`);
    if (!days && minutes % 60) parts.push(`${minutes % 60} ${translations.minutes_short}`);
    return parts.join(' ');
};

const csvTime = (iso) => (iso ? formatTime(iso).replace(',', '') : '');

// Built from the list already loaded; the BOM keeps the umlauts right in Excel.
const toCsv = (outages) => {
    const header = [
        translations.sms_outage_start,
        translations.sms_outage_end,
        translations.sms_outage_duration_minutes,
        translations.sms_outage_error,
    ];
    const rows = outages.map((outage) => [
        csvTime(outage.started_at),
        csvTime(outage.ended_at),
        outage.ended_at
            ? Math.round((new Date(outage.ended_at) - new Date(outage.started_at)) / 60000)
            : '',
        outage.error ?? '',
    ]);
    return '\uFEFF' + [header, ...rows].map((row) => row.join(',')).join('\n');
};

// Rendered into body: the header is fixed and would otherwise trap the modal
// in its own stacking order.
export default function SmsGatewayOutagesModal({ onClose }) {
    useLockBodyScroll();
    const [outages, setOutages] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        API.getOutages()
            .then((res) => setOutages(res.data))
            .catch(() => setFailed(true));
    }, []);

    let content;
    if (failed) {
        content = <p>{translations.sms_outages_failed}</p>;
    } else if (!outages) {
        content = <Loader theme="dark" />;
    } else if (!outages.length) {
        content = <p>{translations.sms_outages_none}</p>;
    } else {
        content = (
            <>
                <div className="export">
                    <ExportBtn
                        apiFunc={async () => ({ data: toCsv(outages) })}
                        fileName="sms-server-ausfaelle.csv"
                    />
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>{translations.sms_outage_start}</th>
                            <th>{translations.sms_outage_end}</th>
                            <th>{translations.sms_outage_duration}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {outages.map((outage) => (
                            <tr key={outage.id} className={outage.ended_at ? '' : 'ongoing'}>
                                <td>{formatTime(outage.started_at)}</td>
                                <td>
                                    {outage.ended_at
                                        ? formatTime(outage.ended_at)
                                        : translations.sms_outage_ongoing}
                                </td>
                                <td>
                                    {formatDuration(
                                        outage.started_at,
                                        outage.ended_at ?? new Date(),
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </>
        );
    }

    return createPortal(
        <Modal title={translations.sms_outages_title} onClose={onClose} maxWidth="560px">
            <div className="SmsGatewayOutages">{content}</div>
        </Modal>,
        document.body,
    );
}
